import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createFoldersApi,
  tacticKey,
  workbookOrigin,
  workbookRunMode,
  workbookUpdateAvailable,
} from '@bloo-team/api-client'
import type {
  Folder,
  WorkbookListFilter,
  WorkbookPage,
  WorkbookRow,
  WorkbookSignalMeta,
} from '@bloo-team/api-client'
import { client, isDemoSession } from '@bloo-team/core-shell'
import { DEMO_ANALYTICS } from './coverage'
import {
  baseRecord,
  demoWorkbookStore,
  liveWorkbooksApi,
  matchesLens,
  normalizeSeverity,
  type DetectionKind,
  type DetectionRecord,
} from './detections'
import type { Workbook, WorkbookOrigin } from '@bloo-team/api-client'

/**
 * Vantage · Detections — the paged library (#205 phase 1).
 *
 * The table's spine is workbook/list, paged and filtered server-side
 * (pageno / pagesize / addfilters, total from `count`). What the backend
 * can filter on goes up as addfilters (`serverFilters`); what it cannot —
 * text, origin, kind, the updates/untagged chips — runs on the loaded page
 * only (`matchesPageLens`) and the UI says so. Sort is client-side, on the
 * page. The stat band, tactic spine and filter option lists keep their
 * existing sources (lib/detections.ts `mergeDetections`), untouched.
 */

/* ----------------------------------------------------------------- filters */

export interface ServerLens {
  /** directory/list folder id; the backend matches that folder's own rows. */
  folderId?: string
  status?: 'enabled' | 'disabled'
  tactic?: string
  /** A technique NAME — the signal block's DetectionTechnique. */
  technique?: string
  stage?: string
}

export function serverFilters(lens: ServerLens): WorkbookListFilter[] {
  const out: WorkbookListFilter[] = []
  if (lens.folderId) out.push({ key: 'path', operand: '=', value: lens.folderId })
  if (lens.status) out.push({ key: 'disabled', operand: '=', value: lens.status === 'disabled' })
  if (lens.tactic) out.push({ key: 'DetectionTactic', operand: '=', value: lens.tactic })
  if (lens.technique) out.push({ key: 'DetectionTechnique', operand: '=', value: lens.technique })
  if (lens.stage) out.push({ key: 'stage', operand: '=', value: lens.stage })
  return out
}

export interface PageLens {
  text?: string
  origin?: WorkbookOrigin
  kind?: DetectionKind
  chip?: 'updates' | 'untagged'
}

/** The filters workbook/list has no key for — applied to the loaded page. */
export function matchesPageLens(d: DetectionRecord, lens: PageLens): boolean {
  if (!matchesLens(d, { ...(lens.text ? { text: lens.text } : {}), ...(lens.origin ? { origin: lens.origin } : {}), ...(lens.kind ? { kind: lens.kind } : {}) })) {
    return false
  }
  if (lens.chip === 'updates' && !d.updateAvailable) return false
  if (lens.chip === 'untagged' && d.techniques.length > 0) return false
  return true
}

/* ------------------------------------------------------------------ record */

const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))]

/**
 * A paged row as a registry record. Severity/score/tactic come from the
 * FIRST signal block that carries them; techniques and sub-techniques from
 * every signal block. Signals 30d / last signal are the row's own fields
 * (null on every captured row — rendered "—").
 */
export function rowRecord(w: WorkbookRow): DetectionRecord {
  const rec = baseRecord(w.id, w.name)
  const first = (pick: (s: WorkbookSignalMeta) => string) => w.signals.map(pick).find(Boolean) ?? ''
  rec.origin = workbookOrigin(w)
  rec.enabled = !w.disabled
  rec.runMode = workbookRunMode(w)
  rec.stage = w.stage
  rec.version = w.version
  rec.updateAvailable = workbookUpdateAvailable(w)
  rec.locked = w.is_locked
  rec.isPrivate = w.is_private
  rec.severity = normalizeSeverity(first((s) => s.severity))
  rec.score = first((s) => s.score)
  rec.tactic = first((s) => s.tactic)
  rec.techniques = uniq(w.signals.map((s) => s.technique))
  rec.subtechniques = uniq(w.signals.map((s) => s.subtechnique))
  rec.tags = w.signal_tags
  rec.folder = w.path.replace(/^\//, '')
  rec.streams = w.stream
  rec.signalCount = w.signalCountRaw ?? 0
  rec.signalCountKnown = w.signalCountRaw !== null
  rec.lastFired = w.signal_last_seen
  rec.kb = w.signal_kb
  rec.updatedAt = w.last_modified_tstamp !== null ? w.last_modified_tstamp * 1000 : null
  return rec
}

/* ------------------------------------------------------------- chip counts */

/**
 * The all / enabled / disabled chip numbers, from the ONE stats call
 * (useWorkbooksOverview) — scope-wide, not narrowed by the table's filters.
 * `disabled` is a strict boolean, so all = enabled + disabled. When the
 * stats call's `count` is above the rows it returned (the default page
 * size cut it), only `all` is exact; enabled/disabled stay blank rather
 * than wrong.
 */
export function chipsFromOverview(
  o: { rows: readonly Pick<Workbook, 'disabled'>[]; total: number } | undefined,
): { all: number | null; enabled: number | null; disabled: number | null } {
  if (!o) return { all: null, enabled: null, disabled: null }
  if (o.rows.length < o.total) return { all: o.total, enabled: null, disabled: null }
  const enabled = o.rows.filter((r) => !r.disabled).length
  return { all: o.total, enabled, disabled: o.total - enabled }
}

/* ----------------------------------------------------------------- columns */

export type DetectionColumnKey =
  | 'severity' | 'streams' | 'signals' | 'lastFired' | 'tags' | 'stage'
  | 'origin' | 'folder' | 'tactic' | 'verdicts' | 'suppressions' | 'version'

/** Name, the status dot and row actions are always on; these are pickable. */
export const DETECTION_COLUMNS: ReadonlyArray<{ key: DetectionColumnKey; label: string; default: boolean }> = [
  { key: 'severity', label: 'Severity', default: true },
  { key: 'streams', label: 'Streams', default: true },
  { key: 'signals', label: 'Signals 30d', default: true },
  { key: 'lastFired', label: 'Last signal', default: true },
  { key: 'tags', label: 'Tags', default: true },
  { key: 'stage', label: 'Stage', default: true },
  { key: 'origin', label: 'Origin', default: false },
  { key: 'folder', label: 'Folder', default: false },
  { key: 'tactic', label: 'Tactic · technique', default: false },
  { key: 'verdicts', label: 'Verdicts 30d', default: false },
  { key: 'suppressions', label: 'Suppressions', default: false },
  { key: 'version', label: 'Version', default: false },
]

export const DEFAULT_COLUMNS: DetectionColumnKey[] = DETECTION_COLUMNS.filter((c) => c.default).map((c) => c.key)

/** Draft store contract, documented in docs/detections.md §Persistence. */
const COLUMNS_KEY = 'bloo.detections.columns'

const inCatalogueOrder = (keys: readonly string[]): DetectionColumnKey[] =>
  DETECTION_COLUMNS.map((c) => c.key).filter((k) => keys.includes(k))

export function readColumns(): DetectionColumnKey[] {
  try {
    const raw = window.localStorage.getItem(COLUMNS_KEY)
    if (!raw) return DEFAULT_COLUMNS
    const parsed: unknown = JSON.parse(raw)
    const visible = (parsed as { visible?: unknown } | null)?.visible
    if (!Array.isArray(visible)) return DEFAULT_COLUMNS
    return inCatalogueOrder(visible.filter((k): k is string => typeof k === 'string'))
  } catch {
    return DEFAULT_COLUMNS
  }
}

export function writeColumns(keys: readonly string[]): void {
  try {
    window.localStorage.setItem(COLUMNS_KEY, JSON.stringify({ v: 1, visible: inCatalogueOrder(keys) }))
  } catch {
    // Preference only.
  }
}

/* --------------------------------------------------------------- folders */

/** Depth-first lookup by id. */
export function findFolder(tree: readonly Folder[], id: string): Folder | null {
  for (const f of tree) {
    if (f.id === id) return f
    const hit = findFolder(f.children, id)
    if (hit) return hit
  }
  return null
}

/** Depth-first lookup by path — the workbook's folder is stored as its path. */
export function findFolderByPath(tree: readonly Folder[], path: string): Folder | null {
  for (const f of tree) {
    if (f.path === path) return f
    const hit = findFolderByPath(f.children, path)
    if (hit) return hit
  }
  return null
}

/**
 * The rail's find box: a folder survives when its name matches or a
 * descendant's does; a matching folder keeps its whole subtree.
 */
export function filterFolderTree(tree: Folder[], query: string): Folder[] {
  const q = query.trim().toLowerCase()
  if (!q) return tree
  const walk = (ns: readonly Folder[]): Folder[] =>
    ns.flatMap((f) => {
      if (f.name.toLowerCase().includes(q)) return [f]
      const children = walk(f.children)
      return children.length ? [{ ...f, children }] : []
    })
  return walk(tree)
}

/** Every folder id in the tree — the find box expands all matches. */
export function folderIds(tree: readonly Folder[]): string[] {
  return tree.flatMap((f) => [f.id, ...folderIds(f.children)])
}

/* --------------------------------------------------------------- demo mode */

const DRAFTS = '/drafts'
const demoFolderId = (path: string) => `demo-dir:${path}`

/**
 * The demo folder tree, derived from the demo rows' paths so every row
 * sits in a real node; `drafts` is always present (legacy: it always
 * exists and cannot be deleted) and sorts first.
 */
export function demoFolderTree(rows: readonly Pick<Workbook, 'path'>[]): Folder[] {
  const roots: Folder[] = []
  const byPath = new Map<string, Folder>()
  const ensure = (path: string): Folder => {
    const hit = byPath.get(path)
    if (hit) return hit
    const parts = path.split('/').filter(Boolean)
    const node: Folder = { id: demoFolderId(path), name: parts[parts.length - 1] ?? path, path, children: [] }
    byPath.set(path, node)
    if (parts.length > 1) ensure(`/${parts.slice(0, -1).join('/')}`).children.push(node)
    else roots.push(node)
    return node
  }
  ensure(DRAFTS)
  for (const r of rows) if (r.path) ensure(r.path)
  const sortTree = (ns: Folder[]) => {
    ns.sort((a, b) => (a.path === DRAFTS ? -1 : b.path === DRAFTS ? 1 : a.name.localeCompare(b.name)))
    ns.forEach((n) => sortTree(n.children))
  }
  sortTree(roots)
  return roots
}

function demoMatches(r: WorkbookRow, f: WorkbookListFilter, tree: readonly Folder[]): boolean {
  const eq = (a: string, b: unknown) => typeof b === 'string' && a.toLowerCase() === b.toLowerCase()
  switch (f.key) {
    case 'path': {
      const folder = typeof f.value === 'string' ? findFolder(tree, f.value) : null
      return folder !== null && r.path === folder.path
    }
    case 'disabled':
      return r.disabled === f.value
    case 'stage':
      return eq(r.stage, f.value)
    case 'stream':
      return Array.isArray(f.value) && f.value.some((s) => r.stream.includes(s))
    case 'DetectionTactic':
      return typeof f.value === 'string' && r.signals.some((s) => tacticKey(s.tactic) === tacticKey(f.value as string))
    case 'DetectionTechnique':
      return r.signals.some((s) => eq(s.technique, f.value))
    case 'DetectionSeverity':
      return r.signals.some((s) => eq(s.severity, f.value))
    case 'id':
      return r.id === f.value
    case 'name':
      return typeof f.value === 'string' && (f.operand === 'LIKE'
        ? r.name.toLowerCase().includes(f.value.toLowerCase())
        : eq(r.name, f.value))
    default:
      return true
  }
}

/** The backend's paging contract over an in-memory row set. */
export function demoWorkbookPage(
  rows: readonly WorkbookRow[],
  tree: readonly Folder[],
  q: { pageNo: number; pageSize: number; filters: readonly WorkbookListFilter[] },
): WorkbookPage {
  const hits = rows.filter((r) => q.filters.every((f) => demoMatches(r, f, tree)))
  const start = (q.pageNo - 1) * q.pageSize
  return { rows: hits.slice(start, start + q.pageSize), total: hits.length }
}

/** Demo rows: the mutable demo workbook store, given the stream + signal
 * block metadata a live row carries (from the same DEMO_ANALYTICS ids). */
function demoRows(): WorkbookRow[] {
  const meta = new Map<string, WorkbookSignalMeta>()
  for (const [technique, dets] of Object.entries(DEMO_ANALYTICS)) {
    for (const d of dets) {
      if (!meta.has(d.id)) {
        meta.set(d.id, { severity: d.severity, score: '', tactic: d.tactic, technique, subtechnique: '' })
      }
    }
  }
  return demoWorkbookStore().map((w) => {
    const m = meta.get(w.id)
    return { ...w, stream: w.stream_flows, signals: m ? [m] : [], signalCountRaw: w.signal_count }
  })
}

/* ------------------------------------------------------------------- hooks */

export const liveFoldersApi = createFoldersApi(client)

type Scope = { clusterId: string; scopeId: string }
const mode = (isDemo: boolean) => (isDemo ? 'demo' : 'live')

export function useFolders(q: Scope) {
  const isDemo = isDemoSession()
  return useQuery<Folder[]>({
    queryKey: ['workbook-folders', q.clusterId, q.scopeId, mode(isDemo)],
    queryFn: () => (isDemo ? Promise.resolve(demoFolderTree(demoWorkbookStore())) : liveFoldersApi.list(q)),
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * One server page. Keyed under the workbooks prefix so the enable/disable
 * mutation's invalidation (lib/detections.ts) refreshes every page.
 */
export function useWorkbookPage(
  q: Scope & { pageNo: number; pageSize: number; filters: WorkbookListFilter[]; enabled?: boolean },
) {
  const isDemo = isDemoSession()
  return useQuery<WorkbookPage>({
    queryKey: ['workbooks', q.clusterId, q.scopeId, mode(isDemo), 'page', q.pageNo, q.pageSize, q.filters],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoWorkbookPage(demoRows(), demoFolderTree(demoWorkbookStore()), q))
        : liveWorkbooksApi.listPage(q),
    enabled: q.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: 1,
  })
}

/** One workbook by id, for a slide-over deep link to a row not on screen. */
export function useWorkbookRow(q: Scope & { id: string | null }) {
  const page = useWorkbookPage({
    ...q,
    pageNo: 1,
    pageSize: 1,
    filters: q.id ? [{ key: 'id', operand: '=', value: q.id }] : [],
    enabled: q.id !== null,
  })
  const row = q.id !== null ? page.data?.rows.find((r) => r.id === q.id) : undefined
  return { record: row ? rowRecord(row) : null, isPending: q.id !== null && page.isPending }
}
